import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  Stack,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Divider,
  Collapse,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  LinearProgress,
  Fade,
  Badge,
  List,
  ListItemButton,
  ListItemText,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Bookmark as BookmarkIcon,
  SmartToy as AIIcon,
  Description as DocIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Gavel as GavelIcon,
  FormatQuote as QuoteIcon,
  NotificationsNone as AlertIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { getUserDocuments } from '../../services/documentService';
import { savePendingCitation } from '../../utils/pendingCitation';

// ─── Types ────────────────────────────────────────────────────────────────────

interface JurisprudenceResult {
  id: string;
  numero_processo: string;
  classe: string;
  assunto: string;
  assuntos_secundarios: string[];
  tribunal: string;
  sigla_tribunal: string;
  orgao_julgador: string;
  relator: string;
  data_ajuizamento: string;
  relevancia: number;
  ementa: string;
  ementa_completa: string;
  desfecho: Andamento | null;
  andamentos: Andamento[];
  total_movimentos: number;
  url_consulta: string;
}

interface Andamento {
  data: string;
  nome: string;
  complemento: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Aceita 'YYYY-MM-DD', ISO completo e o formato cru do DataJud ('20260721000000').
const formatDate = (d: string) => {
  const m = /^(\d{4})-?(\d{2})-?(\d{2})/.exec(d || '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

const formatDesfecho = (a: Andamento) =>
  `${a.nome}${a.complemento ? ` (${a.complemento})` : ''}${a.data ? ` em ${formatDate(a.data)}` : ''}`;

// 20 dígitos → NNNNNNN-DD.AAAA.J.TR.OOOO (Resolução CNJ 65/2008)
const formatCNJ = (n: string) => {
  const d = (n || '').replace(/\D/g, '');
  return d.length === 20 ? `${d.slice(0, 7)}-${d.slice(7, 9)}.${d.slice(9, 13)}.${d.slice(13, 14)}.${d.slice(14, 16)}.${d.slice(16)}` : n;
};

const formatCitation = (r: JurisprudenceResult): string => {
  // Data da decisão quando o DataJud registra um desfecho; senão, a do ajuizamento
  const data = formatDate(r.desfecho?.data || r.data_ajuizamento);
  const relator = r.relator && r.relator !== 'Não informado' ? `. Relator: ${r.relator}` : '';
  return `BRASIL. ${r.tribunal}. ${r.classe}. Processo nº ${formatCNJ(r.numero_processo)}${relator}. ${r.orgao_julgador}${data ? `, ${data}` : ''}.`;
};

const GRAU_OPTIONS = [
  { value: 'G1', label: '1º Grau (G1)' },
  { value: 'G2', label: '2º Grau (G2)' },
  { value: 'JE', label: 'Juizados Especiais' },
  { value: 'SUP', label: 'Superiores' },
];

const YEAR_OPTIONS = [
  { value: '', label: 'Qualquer período' },
  { value: `${new Date().getFullYear() - 1}-01-01`, label: 'Último ano' },
  { value: `${new Date().getFullYear() - 2}-01-01`, label: 'Últimos 2 anos' },
  { value: `${new Date().getFullYear() - 5}-01-01`, label: 'Últimos 5 anos' },
];

const QUICK_SEARCHES = [
  'dano moral',
  'rescisão contratual',
  'responsabilidade civil',
  'execução fiscal',
  'direito do consumidor',
  'acidente de trabalho',
  'indenização',
  'habeas corpus',
];

// ─── Main Component ───────────────────────────────────────────────────────────

const AdvancedJurisprudenceSearch: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [tribunal, setTribunal] = useState('');
  const [grau, setGrau] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [ordenacao, setOrdenacao] = useState<'relevancia' | 'data' | 'tribunal'>('relevancia');
  const [showFilters, setShowFilters] = useState(false);

  const [results, setResults] = useState<JurisprudenceResult[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [savedQueries, setSavedQueries] = useState<string[]>([]);

  // Citation dialog
  const [citationResult, setCitationResult] = useState<JurisprudenceResult | null>(null);
  const [showCitationDialog, setShowCitationDialog] = useState(false);
  const [petitionTarget, setPetitionTarget] = useState<JurisprudenceResult | null>(null);
  const [petitionFilter, setPetitionFilter] = useState('');
  // Só busca as peças quando a janela de "Usar em Petição" é aberta (mesmo cache da página de documentos)
  const { data: userDocuments = [], isLoading: loadingDocuments, isError: documentsError } = useQuery({
    queryKey: ['documents'],
    queryFn: getUserDocuments,
    staleTime: 5 * 60 * 1000,
    enabled: !!petitionTarget,
  });

  // Bookmarks
  const [bookmarks, setBookmarks] = useState<string[]>([]);

  useEffect(() => {
    const saved = localStorage.getItem('juris_bookmarks');
    if (saved) setBookmarks(JSON.parse(saved));
    const savedQ = localStorage.getItem('juris_saved_queries');
    if (savedQ) setSavedQueries(JSON.parse(savedQ));
  }, []);

  const saveQuery = (q: string) => {
    if (!q.trim()) return;
    const next = [q, ...savedQueries.filter((s) => s !== q)].slice(0, 8);
    setSavedQueries(next);
    localStorage.setItem('juris_saved_queries', JSON.stringify(next));
  };

  const toggleBookmark = (id: string) => {
    const next = bookmarks.includes(id)
      ? bookmarks.filter((b) => b !== id)
      : [...bookmarks, id];
    setBookmarks(next);
    localStorage.setItem('juris_bookmarks', JSON.stringify(next));
  };

  // ── Search ────────────────────────────────────────────────────────────────

  const search = useCallback(async () => {
    if (!query.trim()) { setError('Digite um termo para buscar.'); return; }
    setLoading(true);
    setError(null);
    setNotice(null);
    setResults([]);
    setTotalResults(0);
    saveQuery(query.trim());

    try {
      const params: Record<string, string> = {
        query: query.trim(),
        ordenacao,
        size: '10',
      };
      if (tribunal) params.tribunais = tribunal;
      if (grau) params.grau = grau;
      if (dataInicio) params.dataInicial = dataInicio;

      const resp = await api.get('/jurisprudence/advanced-search', { params });
      if (resp.data.status === 'success') {
        const mapped: JurisprudenceResult[] = (resp.data.data || []).map((item: any, idx: number) => ({
          id: item.id || `${item.numero_processo}-${idx}`,
          numero_processo: item.numero_processo || '',
          classe: item.classe || 'Classe não informada',
          assunto: item.assunto || '',
          assuntos_secundarios: item.assuntos_secundarios || [],
          tribunal: item.tribunal || '',
          sigla_tribunal: item.sigla_tribunal || '',
          orgao_julgador: item.orgao_julgador || '',
          relator: item.relator || 'Não informado',
          data_ajuizamento: item.data_ajuizamento || '',
          relevancia: item.relevancia || 0,
          ementa: item.ementa || '',
          ementa_completa: item.ementa_completa || item.ementa || '',
          desfecho: item.desfecho || null,
          andamentos: item.andamentos || [],
          total_movimentos: item.total_movimentos || 0,
          url_consulta: item.url_consulta || `https://www.cnj.jus.br/processo/${item.numero_processo}`,
        }));
        setResults(mapped);
        setTotalResults(resp.data.total || mapped.length);
        // O DataJud (CNJ) oscila: tribunal que não respondeu não pode parecer "nada encontrado"
        const unavailable: string[] = resp.data.tribunais_indisponiveis || [];
        if (mapped.length === 0) {
          setError(unavailable.length
            ? `O DataJud (CNJ) não respondeu a tempo para ${unavailable.join(', ')}. Tente novamente em alguns minutos.`
            : `Nenhum processo encontrado para "${query.trim()}". Tente outros termos ou remova filtros.`);
        } else if (unavailable.length) {
          setNotice(`Resultados parciais: o DataJud não respondeu a tempo para ${unavailable.join(', ')}.`);
        }
      } else {
        setError(resp.data.message || 'Erro na busca.');
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erro ao conectar com o DataJud.');
    } finally {
      setLoading(false);
    }
  }, [query, tribunal, grau, dataInicio, ordenacao]);

  // ── Copy citation ─────────────────────────────────────────────────────────

  const openCitationDialog = (r: JurisprudenceResult) => {
    setCitationResult(r);
    setShowCitationDialog(true);
  };

  const copyCitation = (r: JurisprudenceResult) => {
    navigator.clipboard.writeText(formatCitation(r));
  };

  const copyResumo = (r: JurisprudenceResult) => {
    navigator.clipboard.writeText(r.ementa_completa || r.ementa);
  };

  // ── AI analysis ───────────────────────────────────────────────────────────

  const analyzeWithAI = (r: JurisprudenceResult) => {
    const relator = r.relator && r.relator !== 'Não informado' ? `Relator: ${r.relator}\n` : '';
    const message =
      `Analise esta jurisprudência e explique os pontos principais, a tese jurídica e como ela pode ser aplicada:\n\n` +
      `Tribunal: ${r.tribunal}\nClasse: ${r.classe}\nAssunto: ${r.assunto}\n` +
      `Órgão: ${r.orgao_julgador}\n${relator}` +
      `Ajuizamento: ${formatDate(r.data_ajuizamento)}\n\n` +
      `Resumo do processo (metadados do DataJud/CNJ, sem inteiro teor):\n${r.ementa_completa || r.ementa}`;
    // O chat recebe a mensagem inicial por state (como no painel); ?message= não é lido
    navigate('/chat', { state: { initialMessage: message } });
  };

  // ── Use in document ───────────────────────────────────────────────────────

  const openPetitionDialog = (r: JurisprudenceResult) => {
    setPetitionFilter('');
    setPetitionTarget(r);
  };

  const insertInDocument = (documentId: string) => {
    if (!petitionTarget) return;
    const r = petitionTarget;
    savePendingCitation({
      documentId,
      citation: formatCitation(r),
      desfecho: r.desfecho ? formatDesfecho(r.desfecho) : undefined,
      tribunal: r.tribunal,
      numeroProcesso: r.numero_processo,
    });
    setPetitionTarget(null);
    navigate(`/documents/editor-advanced/${documentId}`);
  };

  const startNewPetition = () => {
    if (petitionTarget) navigator.clipboard.writeText(formatCitation(petitionTarget)).catch(() => {});
    setPetitionTarget(null);
    navigate('/documents/flows');
  };

  const petitionOptions = userDocuments
    .filter((d) => d.title.toLowerCase().includes(petitionFilter.trim().toLowerCase()))
    .sort((a, b) => b.updated_at.getTime() - a.updated_at.getTime())
    .slice(0, 8);

  // ── Monitor process ───────────────────────────────────────────────────────

  const monitorProcess = (r: JurisprudenceResult) => {
    navigate(`/alertas?cnj=${r.numero_processo}&tribunal=${r.sigla_tribunal}`);
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* ── Header ── */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <GavelIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>
            Pesquisa de Jurisprudência
          </Typography>
          <Chip label="DataJud / CNJ" size="small" variant="outlined" color="primary" />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Encontre precedentes, acórdãos e decisões de todos os tribunais brasileiros. Copie citações formatadas e analise com IA.
        </Typography>
      </Box>

      {/* ── Search bar ── */}
      <Paper
        elevation={2}
        sx={{ p: 2, mb: 3, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}
      >
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            fullWidth
            placeholder="Pesquise por assunto, classe processual ou tema (ex.: dano moral)..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && search()}
            sx={{ flex: 1, minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: query && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setQuery('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormControl sx={{ minWidth: 150 }}>
            <Select
              displayEmpty
              value={tribunal}
              onChange={(e) => setTribunal(e.target.value)}
              renderValue={(v) => v || 'Tribunal'}
              sx={{ '& .MuiSelect-select': { py: '13.5px' } }}
            >
              <MenuItem value="">Todos os tribunais</MenuItem>
              <MenuItem disabled><Typography variant="caption" color="text.secondary">─ Superiores ─</Typography></MenuItem>
              {['STJ', 'TST', 'TSE', 'STM'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              <MenuItem disabled><Typography variant="caption" color="text.secondary">─ Estaduais ─</Typography></MenuItem>
              {['TJSP', 'TJRJ', 'TJMG', 'TJRS', 'TJPR', 'TJBA', 'TJDFT', 'TJCE', 'TJSC', 'TJPE'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              <MenuItem disabled><Typography variant="caption" color="text.secondary">─ Federais ─</Typography></MenuItem>
              {['TRF1', 'TRF2', 'TRF3', 'TRF4', 'TRF5', 'TRF6'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 130 }}>
            <Select
              displayEmpty
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              renderValue={(v) => {
                const opt = YEAR_OPTIONS.find((y) => y.value === v);
                return opt?.label || 'Período';
              }}
              sx={{ '& .MuiSelect-select': { py: '13.5px' } }}
            >
              {YEAR_OPTIONS.map((y) => (
                <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
            onClick={search}
            disabled={loading}
            sx={{ minWidth: 110, borderRadius: 2 }}
          >
            Buscar
          </Button>

          <Tooltip title="Filtros avançados">
            <IconButton
              onClick={() => setShowFilters((v) => !v)}
              color={showFilters ? 'primary' : 'default'}
              sx={{
                border: `1px solid ${showFilters ? theme.palette.primary.main : theme.palette.divider}`,
                borderRadius: 2,
              }}
            >
              <FilterIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Advanced filters */}
        <Collapse in={showFilters}>
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={grau}
                    onChange={(e) => setGrau(e.target.value)}
                    renderValue={(v) => GRAU_OPTIONS.find((g) => g.value === v)?.label || 'Grau de jurisdição'}
                  >
                    <MenuItem value="">Todos os graus</MenuItem>
                    {GRAU_OPTIONS.map((g) => <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <Select
                    value={ordenacao}
                    onChange={(e) => setOrdenacao(e.target.value as any)}
                  >
                    <MenuItem value="relevancia">Mais relevantes</MenuItem>
                    <MenuItem value="data">Mais recentes</MenuItem>
                    <MenuItem value="tribunal">Por tribunal</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        {/* Quick search + saved queries */}
        {!query && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              Buscas populares:
            </Typography>
            {QUICK_SEARCHES.map((q) => (
              <Chip
                key={q}
                label={q}
                size="small"
                variant="outlined"
                sx={{ mr: 0.5, mb: 0.5, cursor: 'pointer' }}
                onClick={() => { setQuery(q); setTimeout(() => search(), 100); }}
              />
            ))}
          </Box>
        )}
        {savedQueries.length > 0 && query && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              Buscas salvas:
            </Typography>
            {savedQueries.map((q, i) => (
              <Chip
                key={i}
                label={q}
                size="small"
                variant="filled"
                sx={{ mr: 0.5, mb: 0.5, cursor: 'pointer', bgcolor: alpha(theme.palette.primary.main, 0.08) }}
                onClick={() => { setQuery(q); setTimeout(() => search(), 100); }}
              />
            ))}
          </Box>
        )}
      </Paper>

      {/* ── Error ── */}
      {error && <Alert severity={error.startsWith('Nenhum processo') ? 'info' : 'warning'} sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      {/* ── Loading ── */}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* ── Results header ── */}
      {results.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" color="text.secondary">
            <strong>{totalResults.toLocaleString('pt-BR')}</strong>{' '}
            {totalResults === 1 ? 'resultado encontrado' : 'resultados encontrados'} para{' '}
            <strong>"{query}"</strong>
          </Typography>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={ordenacao}
              onChange={(e) => setOrdenacao(e.target.value as any)}
            >
              <MenuItem value="relevancia">Mais relevantes</MenuItem>
              <MenuItem value="data">Mais recentes</MenuItem>
              <MenuItem value="tribunal">Por tribunal</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {/* ── Result cards ── */}
      <Stack spacing={2.5}>
        {results.map((r) => {
          const isExpanded = expandedId === r.id;
          const isBookmarked = bookmarks.includes(r.id);
          const relevancePct = Math.min(100, Math.round((r.relevancia / 20) * 100));

          return (
            <Paper
              key={r.id}
              elevation={isExpanded ? 3 : 1}
              sx={{
                p: 3,
                borderRadius: 3,
                border: `1px solid ${isExpanded ? theme.palette.primary.main : theme.palette.divider}`,
                transition: 'all 0.2s',
                '&:hover': { boxShadow: theme.shadows[3] },
              }}
            >
              {/* ─ Card header ─ */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  {/* Title line */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                    <Typography
                      variant="h6"
                      component="span"
                      sx={{
                        color: 'primary.main',
                        fontWeight: 700,
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' },
                        fontSize: { xs: '1rem', md: '1.1rem' },
                      }}
                      onClick={() => window.open(r.url_consulta, '_blank')}
                    >
                      {r.tribunal} — {r.classe}
                    </Typography>
                    <Chip label={r.sigla_tribunal || r.tribunal.split(' ')[0]} size="small" color="primary" />
                    {r.assunto && (
                      <Chip label={r.assunto} size="small" variant="outlined" />
                    )}
                  </Box>

                  {/* Meta line */}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {[
                      r.orgao_julgador,
                      r.relator !== 'Não informado' ? `Rel. ${r.relator}` : null,
                      formatDate(r.data_ajuizamento) ? `Ajuizamento: ${formatDate(r.data_ajuizamento)}` : null,
                      r.numero_processo ? `Proc. ${r.numero_processo}` : null,
                    ]
                      .filter(Boolean)
                      .join(' • ')}
                  </Typography>

                  {/* Desfecho (o DataJud não fornece ementa) */}
                  <Typography variant="body2" sx={{ lineHeight: 1.65, color: 'text.primary', mb: 1.5 }}>
                    <Box component="span" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                      Desfecho:{' '}
                    </Box>
                    {r.desfecho ? formatDesfecho(r.desfecho) : 'sem julgamento registrado no DataJud'}
                  </Typography>

                  {/* Secondary subjects */}
                  {r.assuntos_secundarios.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      {r.assuntos_secundarios.map((a, i) => (
                        <Chip key={i} label={a} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5, fontSize: '0.72rem' }} />
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Right bookmarks */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Tooltip title={isBookmarked ? 'Remover favorito' : 'Salvar'}>
                    <IconButton size="small" onClick={() => toggleBookmark(r.id)} color={isBookmarked ? 'warning' : 'default'}>
                      {isBookmarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Abrir no CNJ">
                    <IconButton size="small" component="a" href={r.url_consulta} target="_blank" rel="noopener noreferrer">
                      <OpenIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* ─ Action bar ─ */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  pt: 2,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                {/* PRIMARY: Copy citation */}
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<QuoteIcon />}
                  onClick={() => openCitationDialog(r)}
                  sx={{ fontWeight: 600 }}
                >
                  Copiar Citação
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AIIcon />}
                  onClick={() => analyzeWithAI(r)}
                >
                  Analisar com IA
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DocIcon />}
                  onClick={() => openPetitionDialog(r)}
                >
                  Usar em Petição
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AlertIcon />}
                  onClick={() => monitorProcess(r)}
                >
                  Monitorar
                </Button>

                <Box sx={{ flex: 1 }} />

                {/* Relevance */}
                {r.relevancia > 0 && (
                  <Tooltip title={`Relevância: ${r.relevancia?.toFixed(2)}`}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Relevância
                      </Typography>
                      <Box sx={{ width: 60, bgcolor: 'grey.200', height: 4, borderRadius: 2 }}>
                        <Box sx={{ width: `${relevancePct}%`, bgcolor: 'primary.main', height: '100%', borderRadius: 2 }} />
                      </Box>
                    </Box>
                  </Tooltip>
                )}

                <Button
                  size="small"
                  variant="text"
                  color="primary"
                  endIcon={
                    <ExpandMoreIcon
                      sx={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: '0.2s' }}
                    />
                  }
                  onClick={() => setExpandedId(isExpanded ? null : r.id)}
                >
                  {isExpanded ? 'Menos' : 'Ver andamentos'}
                </Button>
              </Box>

              {/* ─ Andamentos ─ */}
              <Collapse in={isExpanded}>
                <Fade in={isExpanded}>
                  <Box sx={{ mt: 2 }}>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="subtitle2" color="primary" fontWeight={700} gutterBottom>
                      Últimos andamentos
                    </Typography>
                    {r.andamentos.length > 0 ? (
                      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {r.andamentos.map((a, i) => (
                          <Typography component="li" variant="body2" key={i} sx={{ lineHeight: 1.75 }}>
                            <strong>{formatDate(a.data)}</strong> — {a.nome}
                            {a.complemento ? ` (${a.complemento})` : ''}
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2">Nenhum andamento disponível.</Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
                      A base pública do DataJud (CNJ) traz apenas metadados do processo, sem ementa nem inteiro teor.
                      Consulte o site do tribunal para ler a decisão completa.
                    </Typography>
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <Button size="small" variant="outlined" startIcon={<CopyIcon />} onClick={() => copyResumo(r)}>
                        Copiar resumo
                      </Button>
                    </Box>
                  </Box>
                </Fade>
              </Collapse>
            </Paper>
          );
        })}
      </Stack>

      {/* ── Empty state ── */}
      {!loading && results.length === 0 && !error && (
        <Paper
          sx={{
            p: 5,
            textAlign: 'center',
            borderRadius: 3,
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <GavelIcon sx={{ fontSize: 56, color: 'grey.300', mb: 1.5 }} />
          <Typography variant="h6" gutterBottom color="text.secondary">
            Pesquise jurisprudência em todos os tribunais
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Encontre precedentes para suas petições. Use os filtros para refinar por tribunal, grau e período.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
            {QUICK_SEARCHES.map((q) => (
              <Chip
                key={q}
                label={q}
                variant="outlined"
                sx={{ cursor: 'pointer', mb: 1 }}
                onClick={() => { setQuery(q); setTimeout(() => search(), 100); }}
              />
            ))}
          </Stack>
        </Paper>
      )}

      {/* ── Citation Dialog ── */}
      <Dialog open={showCitationDialog} onClose={() => setShowCitationDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <QuoteIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Citação Formatada
        </DialogTitle>
        <DialogContent>
          {citationResult && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Formato ABNT / padrão jurídico brasileiro
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  borderRadius: 2,
                  mb: 2,
                }}
              >
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                  {formatCitation(citationResult)}
                </Typography>
              </Paper>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Resumo do processo (DataJud/CNJ)
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, maxHeight: 200, overflow: 'auto' }}>
                <Typography variant="body2" sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                  {citationResult.ementa_completa || citationResult.ementa}
                </Typography>
              </Paper>

              <Alert severity="success" sx={{ mt: 2 }} icon={<CopyIcon />}>
                Clique em "Copiar" para inserir a citação na sua petição ou documento.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCitationDialog(false)}>Fechar</Button>
          {citationResult && (
            <>
              <Button
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={() => {
                  copyResumo(citationResult);
                  setShowCitationDialog(false);
                }}
              >
                Copiar resumo
              </Button>
              <Button
                variant="contained"
                startIcon={<CopyIcon />}
                onClick={() => {
                  copyCitation(citationResult);
                  setShowCitationDialog(false);
                }}
              >
                Copiar Citação
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      {/* ── Escolher peça para inserir a citação ── */}
      <Dialog open={!!petitionTarget} onClose={() => setPetitionTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Inserir citação em qual peça?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A citação entra no final da peça, com um campo para você colar o trecho da decisão.
            Em "Criar nova peça", a citação é copiada para você colar depois.
          </Typography>
          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder="Buscar peça pelo nome..."
            value={petitionFilter}
            onChange={(e) => setPetitionFilter(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ mb: 1 }}
          />
          {loadingDocuments ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
          ) : documentsError ? (
            <Alert severity="error">Não foi possível carregar suas peças. Tente novamente.</Alert>
          ) : petitionOptions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              {petitionFilter ? 'Nenhuma peça encontrada com esse nome.' : 'Você ainda não tem peças salvas.'}
            </Typography>
          ) : (
            <List dense disablePadding>
              {petitionOptions.map((d) => (
                <ListItemButton key={d.id} onClick={() => insertInDocument(d.id)} sx={{ borderRadius: 1 }}>
                  <ListItemText
                    primary={d.title}
                    secondary={`Atualizada em ${d.updated_at.toLocaleDateString('pt-BR')}`}
                    primaryTypographyProps={{ noWrap: true }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPetitionTarget(null)}>Cancelar</Button>
          <Button variant="outlined" onClick={startNewPetition}>Criar nova peça</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdvancedJurisprudenceSearch;
